/**
 * MODULE: home/Hero.jsx
 * PURPOSE: Homepage hero — headline, "Brief me" archive search, quick country
 *          links. Extracted from the monolithic pages/Home.jsx (Phase 4
 *          split, see /app/memory/ARCHITECTURE_PLAN.md).
 * PUBLIC API: <Hero />
 * DEPENDS ON: utils/api.getCountries, home/PlatformStatsContext.usePlatformStats,
 *   home/homeUtils.flagEmoji
 * CALLED BY: pages/Home.jsx
 * LAST REVIEWED: 2026-07-17
 */
import React, { useState, useEffect } from "react";
import { Search, ArrowRight } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { FORMATTED_DATE } from "../SiteChrome";
import { getCountries } from "../../../utils/api";
import { usePlatformStats } from "./PlatformStatsContext";
import { flagEmoji } from "./homeUtils";

const ROTATING_SEARCH = [
  "Try: Mumbai",
  "Try: Dubai Master Plan 2040",
  "Try: India-Russia Relations",
  "Try: Clevergraph",
  "Try: Aryabhata",
  "Try: Technology",
];

export const Hero = () => {
  const stats = usePlatformStats();
  const [phIndex, setPhIndex] = useState(0);
  const [query, setQuery] = useState("");
  const [quickCountries, setQuickCountries] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const id = setInterval(
      () => setPhIndex((p) => (p + 1) % ROTATING_SEARCH.length),
      2800
    );
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    getCountries().then((d) => {
      const list = (d?.countries || []).filter((c) => c.is_fully_seeded);
      list.sort((a, b) => (b.signals_today || 0) - (a.signals_today || 0));
      setQuickCountries(list.slice(0, 8));
    }).catch(() => {});
  }, []);

  const submitSearch = () => {
    const q = (query || "").trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : "/search");
  };

  return (
    <section
      className="border-b border-[var(--hairline)]"
      data-testid="hero-section"
    >
      <div className="mx-auto max-w-[1100px] px-4 sm:px-6 lg:px-10 pt-10 sm:pt-14 lg:pt-16 pb-10 sm:pb-14 text-center">
        <div
          className="font-mono text-[11px] sm:text-xs tracking-[0.22em] uppercase text-[var(--muted)]"
          data-testid="hero-dateline"
        >
          Today · {FORMATTED_DATE} · Global Edition
        </div>

        <h1
          className="font-serif text-[44px] leading-[1.02] sm:text-[64px] lg:text-[88px] lg:leading-[0.98] tracking-[-0.025em] text-[var(--ink)] mt-6"
          data-testid="hero-headline"
        >
          Understand any country
          <br className="hidden sm:block" /> in{" "}
          <span className="italic text-[var(--crimson)]">10 minutes.</span>
        </h1>

        <p
          className="font-sans text-[15px] sm:text-lg text-[var(--ink-2)] leading-[1.6] mt-6 max-w-2xl mx-auto"
          data-testid="hero-subhead"
        >
          Daily intelligence briefs with confidence layers, source diversity and
          editorial synthesis. From BBC precision to Economist structure —
          curated for decision-makers.
        </p>

        {/* Search */}
        <div
          className="mt-10 sm:mt-12 max-w-[720px] mx-auto"
          data-testid="hero-search"
        >
          <div className="relative">
            <div className="absolute left-5 top-1/2 -translate-y-1/2 text-[var(--muted)] pointer-events-none">
              <Search size={20} strokeWidth={1.75} />
            </div>
            <Input
              type="text"
              aria-label="Search DNH news"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") submitSearch();
              }}
              placeholder={ROTATING_SEARCH[phIndex]}
              className="h-16 sm:h-[68px] pl-12 pr-32 sm:pr-40 rounded-[12px] border border-[var(--hairline)] bg-[var(--paper)] text-[15px] sm:text-base placeholder:text-[var(--muted)] focus-visible:ring-2 focus-visible:ring-[var(--crimson)] focus-visible:ring-offset-0"
              data-testid="hero-search-input"
            />
            <Button
              type="button"
              onClick={submitSearch}
              className="absolute right-2 top-1/2 -translate-y-1/2 h-12 sm:h-[52px] px-3 sm:px-6 rounded-[12px] bg-[var(--ink)] text-[var(--paper)] hover:bg-[var(--crimson)] font-medium text-[13.5px] sm:text-sm"
              data-testid="hero-search-submit"
            >
              Search
              <ArrowRight size={16} strokeWidth={2} className="ml-2" />
            </Button>
          </div>
          <div className="mt-3 font-mono text-[11px] text-[var(--muted)] flex items-center justify-center gap-2 flex-wrap" data-testid="hero-search-scope">
            <span className="text-[var(--crimson)] font-semibold">DNH Search</span>
            <span className="text-[var(--hairline-strong)]">·</span>
            <span>topics</span>
            <span className="text-[var(--hairline-strong)]">·</span>
            <span>countries</span>
            <span className="text-[var(--hairline-strong)]">·</span>
            <span>industries</span>
            <span className="text-[var(--hairline-strong)]">·</span>
            <span>events</span>
          </div>
        </div>

        {/* Quick country links — read that country's own news, no lens toggle */}
        <div
          className="mt-10 sm:mt-12 flex items-center justify-center gap-2 sm:gap-3 flex-wrap"
          data-testid="hero-country-chips"
        >
          {quickCountries.map((c) => (
            <Link
              key={c.slug}
              to={`/country/${c.slug}`}
              className="dnh-chip inline-flex items-center gap-2 h-10 px-4 rounded-full border border-[var(--hairline)] bg-[var(--paper)] text-[var(--ink)] hover:border-[var(--hairline-strong)] transition-colors text-[13.5px] font-medium"
              data-testid={`hero-chip-${c.slug}`}
            >
              <span className="text-[15px] leading-none">{flagEmoji(c.iso2)}</span>
              <span>{c.name}</span>
            </Link>
          ))}
          <Link
            to="/explore?view=countries"
            data-testid="hero-browse-countries"
            className="inline-flex items-center gap-1.5 h-10 px-3 text-[13.5px] font-medium text-[var(--ink-2)] hover:text-[var(--crimson)] dnh-link-underline"
          >
            Browse all {stats?.countries_count ?? "…"}
            <ArrowRight size={14} strokeWidth={2} />
          </Link>
        </div>
      </div>
    </section>
  );
};
